import type {
  CustomFilter,
  CustomFilterCondition,
  CustomFilterConditionType,
  DataBuilderCompanySet,
  DataBuilderCustomColumn,
  DataBuilderCustomColumnStep,
  DataBuilderSettingValue,
  DatasetInstance,
  DatasetInstanceConfig,
  DeliveryFormat,
  DeliveryMethod,
  ModelVersionSelections,
  S3DeliveryConfig,
  SelectionListSelectedItem,
  SftpDeliveryConfig,
  SnowflakeDeliveryConfig,
} from '../data-builder.store';
import type {
  CompanyMapperSelectionType,
  DataBuilderColumnsWithCustom,
  DataBuilderCompanyMapper,
  DataBuilderDeliverableInput,
  DataBuilderFilterInput,
  DataBuilderLeafFilterInput,
  DataBuilderPayload,
} from './data-builder-api.types';

type HydratedState = {
  datasetInstances: DatasetInstance[];
  activeInstanceId: string | null;
  configByInstance: Record<string, DatasetInstanceConfig>;
  lagData: number;
  deliveryMethod: DeliveryMethod;
  deliveryFormat: DeliveryFormat;
  snowflakeConfig: SnowflakeDeliveryConfig | null;
  s3Config: S3DeliveryConfig | null;
  sftpConfig: SftpDeliveryConfig | null;
  modelVersions: ModelVersionSelections;
  lastPruneEvent: null;
  deliverableName: string;
  deliverableClient: string;
};

const SELECTION_LIST_OPERATORS = new Set(['IN_SET', 'NOT_IN_SET']);
const BOOLEAN_OPERATORS = new Set([
  'IS_TRUE',
  'IS_FALSE',
  'IS_NULL',
  'IS_NOT_NULL',
]);

const DATE_OPERATORS = new Set([
  'GREATER_THAN_OR_EQUAL',
  'LESS_THAN_OR_EQUAL',
  'IN_BETWEEN',
]);

function inferConditionType(filter: {
  operator: string;
  value?: unknown[] | null;
}): CustomFilterConditionType {
  const values = Array.isArray(filter.value) ? filter.value : [];
  if (BOOLEAN_OPERATORS.has(filter.operator)) {
    return 'boolean';
  }
  if (SELECTION_LIST_OPERATORS.has(filter.operator)) {
    return 'selection_list';
  }
  if (
    DATE_OPERATORS.has(filter.operator) &&
    values.every((v) => typeof v === 'string')
  ) {
    return 'date';
  }
  if (values.every((v) => typeof v === 'number')) {
    return 'numeric';
  }
  return 'string';
}

function isCompoundFilter(
  filter: DataBuilderFilterInput
): filter is { type: 'and' | 'or'; filters: DataBuilderFilterInput[] } {
  return (
    'type' in filter &&
    (filter.type === 'and' || filter.type === 'or') &&
    'filters' in filter
  );
}

function isLeafFilter(
  filter: DataBuilderFilterInput
): filter is DataBuilderLeafFilterInput {
  return 'field' in filter && 'operator' in filter;
}

function leafFilterToCondition(
  filter: DataBuilderLeafFilterInput
): CustomFilterCondition {
  const conditionType = inferConditionType(filter);
  const values =
    'value' in filter && Array.isArray(filter.value)
      ? filter.value.map(String)
      : [];
  const groupKey =
    'group_key' in filter
      ? ((filter.group_key as string | null) ?? null)
      : null;

  const base: CustomFilterCondition = {
    type: conditionType,
    field: filter.field,
    groupKey,
    operator: filter.operator as CustomFilterCondition['operator'],
    value: values,
    modifier: 'modifier' in filter ? (filter.modifier ?? null) : null,
  };

  if (conditionType === 'selection_list') {
    const selectionListItems: SelectionListSelectedItem[] = values.map(
      (id) => ({
        id,
        label: id,
        selectionListId: filter.field,
      })
    );
    return { ...base, selectionListItems };
  }

  return base;
}

function compoundFilterChildToConditions(
  child: DataBuilderFilterInput
): CustomFilterCondition[] {
  if (isLeafFilter(child)) {
    return [leafFilterToCondition(child)];
  }

  // A nested OR of selection-list leaves represents a single multi-key
  // selection list condition (e.g. region + country + metro_area).
  if (
    isCompoundFilter(child) &&
    isMultiKeySelectionListOrGroup(child.filters)
  ) {
    return [mergeSelectionListLeafsToCondition(child.filters)];
  }

  return [];
}

function filterPayloadToCustomFilter(
  filter: DataBuilderFilterInput
): CustomFilter {
  if (isCompoundFilter(filter)) {
    const name =
      'name' in filter && typeof filter.name === 'string' ? filter.name : '';

    // An OR of all selection-list leaves is a single multi-key condition
    if (
      filter.type === 'or' &&
      isMultiKeySelectionListOrGroup(filter.filters)
    ) {
      return {
        id: crypto.randomUUID(),
        name,
        conditions: [mergeSelectionListLeafsToCondition(filter.filters)],
        binary: null,
      };
    }

    const conditions = filter.filters.flatMap(compoundFilterChildToConditions);
    return {
      id: crypto.randomUUID(),
      name,
      conditions,
      binary: filter.type === 'or' ? 'OR' : 'AND',
    };
  }

  if (isLeafFilter(filter)) {
    return {
      id: crypto.randomUUID(),
      name: '',
      conditions: [leafFilterToCondition(filter)],
      binary: null,
    };
  }

  return {
    id: crypto.randomUUID(),
    name: '',
    conditions: [],
    binary: null,
  };
}

/**
 * Checks whether all leaf filters in an OR group are selection-list conditions
 * (IN_SET/NOT_IN_SET) that should be recombined into a single multi-key condition.
 * This is the inverse of `buildSelectionListConditions` which splits a single
 * multi-key selection-list condition into separate leaf filters per key.
 */
function isMultiKeySelectionListOrGroup(
  filters: DataBuilderFilterInput[]
): filters is DataBuilderLeafFilterInput[] {
  return (
    filters.length > 1 &&
    filters.every(
      (f) =>
        isLeafFilter(f) &&
        SELECTION_LIST_OPERATORS.has((f as DataBuilderLeafFilterInput).operator)
    )
  );
}

function mergeSelectionListLeafsToCondition(
  leafFilters: DataBuilderLeafFilterInput[]
): CustomFilterCondition {
  const firstFilter = leafFilters[0];
  const operator = (firstFilter?.operator ??
    'IN_SET') as CustomFilterCondition['operator'];

  const allItems: SelectionListSelectedItem[] = [];
  const allValues: string[] = [];

  for (const leaf of leafFilters) {
    const values =
      'value' in leaf && Array.isArray(leaf.value)
        ? leaf.value.map(String)
        : [];
    for (const id of values) {
      allItems.push({
        id,
        label: id,
        selectionListId: leaf.field,
      });
      allValues.push(id);
    }
  }

  const groupKey =
    leafFilters
      .map((leaf) =>
        'group_key' in leaf ? ((leaf.group_key as string | null) ?? null) : null
      )
      .find((value) => value !== null && value !== undefined) ?? null;

  return {
    type: 'selection_list',
    field: firstFilter?.field ?? '',
    groupKey,
    operator,
    value: allValues,
    modifier: firstFilter?.modifier ?? null,
    selectionListItems: allItems,
  };
}

function hasNonEmptyName(value: DataBuilderFilterInput): boolean {
  return 'name' in value && typeof value.name === 'string' && value.name !== '';
}

function isMultiFilterAndWrapper(filter: DataBuilderFilterInput): boolean {
  if (!isCompoundFilter(filter) || filter.type !== 'and') {
    return false;
  }
  if (hasNonEmptyName(filter)) {
    return false;
  }
  if (filter.filters.length < 2) {
    return false;
  }
  return filter.filters.some((child) => {
    if (!isCompoundFilter(child)) {
      return false;
    }
    if (hasNonEmptyName(child)) {
      return true;
    }
    if (child.type === 'or' && !isMultiKeySelectionListOrGroup(child.filters)) {
      return true;
    }
    return false;
  });
}

function filtersPayloadToCustomFilters(
  filter: DataBuilderFilterInput | null | undefined
): CustomFilter[] {
  if (filter === null || filter === undefined) {
    return [];
  }

  if (
    isCompoundFilter(filter) &&
    filter.type === 'or' &&
    filter.filters.every(
      (f) => isLeafFilter(f) || (isCompoundFilter(f) && f.type === 'and')
    )
  ) {
    // Multi-key selection list OR groups are handled by
    // filterPayloadToCustomFilter (merged into a single condition).
    // Other OR groups with mixed children represent separate filters.
    if (isMultiKeySelectionListOrGroup(filter.filters)) {
      return [filterPayloadToCustomFilter(filter)];
    }
    return filter.filters.map(filterPayloadToCustomFilter);
  }

  if (isCompoundFilter(filter) && isMultiFilterAndWrapper(filter)) {
    return filter.filters.map(filterPayloadToCustomFilter);
  }

  return [filterPayloadToCustomFilter(filter)];
}

function customColumnStepCaseToFilter(
  stepCase: DataBuilderFilterInput | null | undefined
): CustomFilter | null {
  if (stepCase === null || stepCase === undefined) {
    return null;
  }
  return filterPayloadToCustomFilter(stepCase);
}

function reverseCustomColumns(
  custom: DataBuilderColumnsWithCustom['custom']
): DataBuilderCustomColumn[] {
  if (custom === null || custom === undefined) {
    return [];
  }

  return custom.map((column) => ({
    id: crypto.randomUUID(),
    name: column.name,
    steps: column.steps.map(
      (step): DataBuilderCustomColumnStep => ({
        case: customColumnStepCaseToFilter(
          step.case as DataBuilderFilterInput | null | undefined
        ),
        then: step.then,
      })
    ),
  }));
}

function hydrateDelivery(deliverable: DataBuilderDeliverableInput): {
  deliveryMethod: DeliveryMethod;
  deliveryFormat: DeliveryFormat;
  snowflakeConfig: SnowflakeDeliveryConfig | null;
  s3Config: S3DeliveryConfig | null;
  sftpConfig: SftpDeliveryConfig | null;
} {
  const delivery = deliverable.delivery as {
    type: string;
    [key: string]: unknown;
  };

  if (delivery.type === 'snowflake') {
    return {
      deliveryMethod: 'snowflake',
      deliveryFormat: 'csv',
      snowflakeConfig: {
        database: delivery.database as string,
        schema: delivery.schema as string,
        table: delivery.table as string,
      },
      s3Config: null,
      sftpConfig: null,
    };
  }

  if (delivery.type === 's3') {
    const format = (delivery.format as DeliveryFormat) ?? 'csv';
    return {
      deliveryMethod: 's3',
      deliveryFormat: format,
      snowflakeConfig: null,
      s3Config: {
        bucket: delivery.bucket as string,
        path: delivery.path as string,
        format,
      },
      sftpConfig: null,
    };
  }

  if (delivery.type === 'sftp') {
    const format = (delivery.format as DeliveryFormat) ?? 'csv';
    return {
      deliveryMethod: 'sftp',
      deliveryFormat: format,
      snowflakeConfig: null,
      s3Config: null,
      sftpConfig: {
        path: delivery.path as string,
        format,
      },
    };
  }

  return {
    deliveryMethod: 'download',
    deliveryFormat: (delivery.format as DeliveryFormat) ?? 'csv',
    snowflakeConfig: null,
    s3Config: null,
    sftpConfig: null,
  };
}

function isUoaCompanyValue(
  value: unknown
): value is { type: string; [key: string]: unknown } {
  return (
    value !== null &&
    value !== undefined &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'type' in value
  );
}

function hydrateInstanceConfig(
  deliverable: DataBuilderDeliverableInput
): DatasetInstanceConfig {
  const d = deliverable as Record<string, unknown>;

  const settings: Record<string, DataBuilderSettingValue> =
    d.settings !== null &&
    d.settings !== undefined &&
    typeof d.settings === 'object'
      ? (d.settings as Record<string, DataBuilderSettingValue>)
      : {};

  const rawUoa = d.unit_of_analysis;
  let unitOfAnalysis: string | null = null;
  let unitOfAnalysisSelections: SelectionListSelectedItem[] = [];
  let companySets: DataBuilderCompanySet[] | null = null;
  let companyMapper: DataBuilderCompanyMapper | null = null;

  if (
    rawUoa !== null &&
    rawUoa !== undefined &&
    typeof rawUoa === 'object' &&
    !Array.isArray(rawUoa)
  ) {
    const obj = rawUoa as {
      type: string;
      value?: { type?: string; [key: string]: unknown } | unknown[];
    };
    unitOfAnalysis = obj.type;

    if (obj.type === 'company' && isUoaCompanyValue(obj.value)) {
      if (obj.value.type === 'company_mapper') {
        const rawSelectionType = obj.value.selection_type;
        companyMapper = {
          type: 'company_mapper' as const,
          pipeline_input: obj.value.pipeline_input as string,
          company_reference: obj.value.company_reference as string,
          ...(typeof rawSelectionType === 'string'
            ? {
                selection_type: rawSelectionType as CompanyMapperSelectionType,
              }
            : {}),
        };
      } else if (obj.value.type === 'company_sets') {
        companySets =
          (obj.value.company_sets as DataBuilderCompanySet[]) ?? null;
      }
    } else if (
      obj.type !== 'company' &&
      obj.type !== 'individual' &&
      Array.isArray(obj.value)
    ) {
      unitOfAnalysisSelections = (
        obj.value as { key: string; value: string[] }[]
      ).flatMap((entry) => {
        if (
          entry === null ||
          typeof entry !== 'object' ||
          typeof entry.key !== 'string' ||
          !Array.isArray(entry.value)
        ) {
          return [];
        }
        return entry.value.map((id) => ({
          id: String(id),
          label: String(id),
          selectionListId: entry.key,
        }));
      });
    }
  }

  const columnKeysByBreakdown: Record<string, string[]> = {};
  const customFiltersByBreakdown: Record<string, CustomFilter[]> = {};
  const customColumnsByBreakdown: Record<string, DataBuilderCustomColumn[]> =
    {};

  const aggregation = d.aggregation as
    | DataBuilderColumnsWithCustom
    | null
    | undefined;
  if (aggregation !== null && aggregation !== undefined) {
    if (aggregation.preset != null && aggregation.preset.length > 0) {
      columnKeysByBreakdown['aggregation'] = aggregation.preset;
    }
    const aggCustomColumns = reverseCustomColumns(aggregation.custom);
    if (aggCustomColumns.length > 0) {
      customColumnsByBreakdown['aggregation'] = aggCustomColumns;
    }
  }

  const files = d.files as
    | {
        type: string;
        filters?: DataBuilderFilterInput | null;
        columns?: DataBuilderColumnsWithCustom;
      }[]
    | undefined;
  const savedFileTypes: string[] = [];
  if (Array.isArray(files)) {
    for (const file of files) {
      const fileKey = file.type ?? '';
      if (fileKey === '') {
        continue;
      }

      savedFileTypes.push(fileKey);

      if (file.columns?.preset != null && file.columns.preset.length > 0) {
        columnKeysByBreakdown[fileKey] = file.columns.preset;
      }

      const fileFilters = filtersPayloadToCustomFilters(file.filters);
      if (fileFilters.length > 0) {
        customFiltersByBreakdown[fileKey] = fileFilters;
      }

      const fileCustomColumns = reverseCustomColumns(
        file.columns?.custom ?? null
      );
      if (fileCustomColumns.length > 0) {
        customColumnsByBreakdown[fileKey] = fileCustomColumns;
      }
    }
  }

  const customFilters = filtersPayloadToCustomFilters(
    d.filters as DataBuilderFilterInput | null | undefined
  );

  return {
    settings,
    unitOfAnalysis,
    unitOfAnalysisSelections,
    columnKeysByBreakdown,
    companySelections: {},
    companySets,
    companyMapper,
    customFilters,
    customFiltersByBreakdown,
    customColumnsByBreakdown,
    includedBreakdownIds: [...new Set(savedFileTypes)],
  };
}

export function hydrateStoreFromPayload(
  payload: DataBuilderPayload
): HydratedState {
  const deliverables = payload.deliverables;

  const datasetInstances: DatasetInstance[] = deliverables.map(
    (deliverable) => ({
      instanceId: crypto.randomUUID(),
      datasetKey: deliverable.type,
      customLabel: deliverable.name,
    })
  );

  const configByInstance: Record<string, DatasetInstanceConfig> = {};
  for (let i = 0; i < deliverables.length; i++) {
    const instance = datasetInstances[i];
    const deliverable = deliverables[i];
    if (instance !== undefined && deliverable !== undefined) {
      configByInstance[instance.instanceId] =
        hydrateInstanceConfig(deliverable);
    }
  }

  const firstDeliverable = deliverables[0];
  const deliveryState =
    firstDeliverable !== undefined
      ? hydrateDelivery(firstDeliverable)
      : {
          deliveryMethod: 'download' as const,
          deliveryFormat: 'csv' as DeliveryFormat,
          snowflakeConfig: null,
          s3Config: null,
          sftpConfig: null,
        };

  const modelVersions: ModelVersionSelections =
    firstDeliverable !== undefined
      ? (firstDeliverable.models as ModelVersionSelections)
      : {};

  return {
    datasetInstances,
    activeInstanceId: datasetInstances[0]?.instanceId ?? null,
    configByInstance,
    lagData: payload.settings?.lag_data ?? 0,
    ...deliveryState,
    modelVersions,
    lastPruneEvent: null,
    deliverableName: payload.name,
    deliverableClient:
      payload.client !== null && payload.client !== undefined
        ? payload.client
        : '',
  };
}
